import * as React from 'react';
import Slider from 'react-input-slider';

import { SoundLightSettings } from "../../types";
import withFormWrapper from "@Elements/Form/formWrapper";
import { WrappedComponentProps, injectIntl, MessageDescriptor } from 'react-intl';
import styled, { ThemeContext } from 'styled-components';
import { Field } from 'formik';

interface ValueSliderProps extends WrappedComponentProps{
  name: string
  limit: number[]
  title?: MessageDescriptor;
  parent?: string;
}

const Row = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  padding: 16px 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  transition: opacity 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.label`
  margin: 0;
  flex: 1 1 130px;
  min-width: 0;
  font-size: ${props => props.theme.font.normal};
  font-weight: 400;
  color: ${props => props.theme.colors.textPrimary};
`;

const Control = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 2 1 220px;
  min-width: 0;
`;

const SliderWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const ValueField = styled(Field)`
  width: 64px;
  height: 38px;
  flex-shrink: 0;
  text-align: center;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
  background: ${props => props.theme.colors.backgroundPrimary};
  border: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.colors.borderRadius};
  outline: none;
  padding: 0 8px;
  transition: border-color 0.15s ease;

  &:focus {
    border: 1px solid ${props => props.theme.colors.accent};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ValueSlider = injectIntl(
  withFormWrapper<SoundLightSettings, ValueSliderProps>(({ formik: { values, setFieldValue }, intl: { formatMessage }, limit, name, title, parent}) => {

    const theme = React.useContext(ThemeContext);

    let disabled = false;

    if(parent){
      const parentvalue = values[parent];
      if(parentvalue === false){
        disabled = true;
      }
    }

    const value = parseInt(values[name] as string, 10) || 0;
    const [min, max] = limit;
    const accent = disabled ? theme.colors.borderColor : theme.colors.accent;

    return (
      <Row disabled={disabled}>
        {title && <Label htmlFor={name}>{formatMessage(title)}</Label>}
        <Control>
          <SliderWrap>
            <Slider
              disabled={disabled}
              axis="x"
              xmin={min}
              xmax={max}
              xstep={1}
              x={value}
              onChange={({x}) => setFieldValue(name, `${x}`)}
              styles={{
                track: {
                  width: '100%',
                  height: 6,
                  borderRadius: 999,
                  backgroundColor: theme.colors.backgroundTertiary
                },
                active: {
                  backgroundColor: accent,
                  borderRadius: 999
                },
                thumb: {
                  width: 18,
                  height: 18,
                  backgroundColor: '#fff',
                  border: `2px solid ${accent}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
                }
              }}
            />
          </SliderWrap>
          <ValueField id={name} disabled={disabled} type="number" min={min} max={max} name={name}/>
        </Control>
      </Row>
    );
  })
)

export default ValueSlider;